import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError, useLazyQuery } from '@apollo/client';
import {
  AiAgentsNamespaceGetAiAgentArgs,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { CloseConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import {
  ConfirmationModalDeleteFooter,
  ConfirmationModalMessage,
  ConfirmationModalTitle,
} from 'src/components/pages/ai-agents/components/confirmation-modal-parts';
import { AI_AGENT_PRIVATE_KNOWLEDGE_BASE_USAGE_QUERY } from 'src/components/pages/ai-agents/knowledge-bases/queries';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

export const useAgentSourceDeleteDialog = () => {
  const { t } = useTranslation(['aiAgents']);
  const { id: companyId } = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const [queryUsage] = useLazyQuery<
    Pick<Query, 'aiAgents'>,
    AiAgentsNamespaceGetAiAgentArgs
  >(AI_AGENT_PRIVATE_KNOWLEDGE_BASE_USAGE_QUERY, { fetchPolicy: 'network-only' });

  return useCallback(
    async ({
      aiAgentId,
      sourceId,
      onConfirm,
    }: {
      aiAgentId: string;
      sourceId: string;
      onConfirm: VoidFunction;
    }) => {
      let sources: { id: string }[];

      try {
        const { data } = await queryUsage({
          variables: { input: { companyId, id: aiAgentId } },
        });
        sources =
          data?.aiAgents.getAIAgent?.privateKnowledgeBase.usage.references.nodes.find(
            ({ agent }) => agent.id === aiAgentId,
          )?.sources ?? [];
      } catch (error) {
        if (!(error instanceof ApolloError)) {
          throw error;
        }

        handleGraphQLError(error);

        return null;
      }

      if (sources.some(({ id }) => id === sourceId)) {
        return {
          title: (
            <ConfirmationModalTitle>
              {t('aiAgents:knowledge.sourceDelete.blocked.title')}
            </ConfirmationModalTitle>
          ),
          message: (
            <ConfirmationModalMessage>
              <p>{t('aiAgents:knowledge.sourceDelete.blocked.inUse')}</p>
              <p>{t('aiAgents:knowledge.sourceDelete.blocked.removeFirst')}</p>
              <p>{t('aiAgents:knowledge.sourceDelete.blocked.prevent')}</p>
            </ConfirmationModalMessage>
          ),
          footerContent: () => null,
        };
      }

      return {
        title: (
          <ConfirmationModalTitle>
            {t('aiAgents:knowledge.sourceDelete.title')}
          </ConfirmationModalTitle>
        ),
        message: (
          <ConfirmationModalMessage>
            <p>{t('aiAgents:knowledge.sourceDelete.notInUse')}</p>
            <p>{t('aiAgents:knowledge.sourceDelete.permanent')}</p>
            <p>{t('aiAgents:knowledge.sourceDelete.message')}</p>
          </ConfirmationModalMessage>
        ),
        footerContent: ({ onClose }: { onClose: CloseConfirmationModal }) => (
          <ConfirmationModalDeleteFooter
            confirmLabel={t('aiAgents:knowledge.sourceDelete.confirm')}
            onConfirm={onConfirm}
            onClose={onClose}
          />
        ),
      };
    },
    [companyId, handleGraphQLError, queryUsage, t],
  );
};
